import {
  FINANCE_SET_ACCOUNT,
  FINANCE_SET_BALANCE,
  FINANCE_SET_TRANSACTIONS,
  FINANCE_SET_INVOICES,
  FINANCE_SET_PAYOUTS,
  FINANCE_SET_SUMMARY,
  FINANCE_SET_LOADING,
  FINANCE_SET_ERROR,
  FINANCE_CLEAR,
  FINANCE_SET_EXPENSES,
  FINANCE_SET_BILLING_RATES,
  FINANCE_SET_DISTRIBUTIONS,
  FINANCE_SET_SELECTED_INVOICE,
  FINANCE_SET_EXPENSE_SUMMARY,
  FINANCE_SET_UNBILLED_EXPENSES,
} from '../types';

export interface FinanceAccount {
  id: number;
  organization_id: number;
  stripe_connect_account_id: string;
  status: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  default_currency: string;
  country: string;
  business_name: string | null;
  bank_name: string | null;
  bank_last4: string | null;
  capabilities: Record<string, string>;
  created_at: string;
}

export interface FinanceBalance {
  available: { amount: number; currency: string }[];
  pending: { amount: number; currency: string }[];
  connect_reserved?: { amount: number; currency: string }[];
}

export interface FinanceTransaction {
  id: number;
  type: string;
  amount: number;
  currency: string;
  net_amount: number;
  fee_amount: number;
  status: string;
  description: string | null;
  client_id: number | null;
  matter_id: number | null;
  invoice_id: number | null;
  created_at: string;
}

export interface FinanceInvoice {
  id: number;
  organization_id: number;
  client_id: number | null;
  matter_id: number | null;
  invoice_number: string;
  status: string;
  currency: string;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  amount_paid: number;
  amount_due: number;
  due_date: string | null;
  paid_at: string | null;
  sent_at: string | null;
  voided_at: string | null;
  viewed_at: string | null;
  notes: string | null;
  payment_method: string | null;
  stripe_payment_link: string | null;
  line_items: { description: string; amount: number; quantity: number }[];
  tax_rate: number | null;
  discount_rate: number | null;
  internal_memo: string | null;
  billing_address: Record<string, any> | null;
  client_name: string | null;
  client_email: string | null;
  matter_title: string | null;
  payment_terms: string | null;
  is_recurring: boolean;
  recurring_interval: string | null;
  next_invoice_date: string | null;
  reminder_sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinanceExpense {
  id: number;
  organization_id: number;
  matter_id: number | null;
  client_id: number | null;
  user_id: number | null;
  category: string;
  description: string | null;
  amount: number;
  currency: string;
  receipt_url: string | null;
  is_billable: boolean;
  invoice_id: number | null;
  status: string;
  expense_date: string | null;
  approved_by_user_id: number | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinanceBillingRate {
  id: number;
  organization_id: number;
  user_id: number | null;
  client_id: number | null;
  matter_id: number | null;
  rate_type: string;
  amount: number;
  currency: string;
  description: string | null;
  is_default: boolean;
  effective_from: string | null;
  effective_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinanceDistribution {
  id: number;
  organization_id: number;
  payout_id: number | null;
  user_id: number;
  matter_id: number | null;
  invoice_id: number | null;
  amount: number;
  currency: string;
  type: string;
  status: string;
  notes: string | null;
  approved_by_user_id: number | null;
  approved_at: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExpenseSummary {
  total_expenses: number;
  total_billable: number;
  total_non_billable: number;
  by_category: Record<string, number>;
  by_status: Record<string, number>;
  currency: string;
}

export interface FinancePayout {
  id: number;
  stripe_payout_id: string;
  amount: number;
  currency: string;
  status: string;
  method: string | null;
  arrival_date: string | null;
  bank_account_last4: string | null;
  description: string | null;
  failure_code: string | null;
  failure_message: string | null;
  created_at: string;
}

export interface FinanceSummary {
  total_revenue: number;
  total_payouts: number;
  total_refunds: number;
  total_fees: number;
  outstanding: number;
  net_balance: number;
  currency: string;
  period_days: number;
  transaction_count: number;
}

export interface FinanceState {
  account: FinanceAccount | null;
  balance: FinanceBalance | null;
  transactions: {
    items: FinanceTransaction[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
  invoices: {
    items: FinanceInvoice[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
  payouts: {
    items: FinancePayout[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
  expenses: {
    items: FinanceExpense[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
  billingRates: FinanceBillingRate[];
  distributions: {
    items: FinanceDistribution[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
  selectedInvoice: FinanceInvoice | null;
  expenseSummary: ExpenseSummary | null;
  unbilledExpenses: FinanceExpense[];
  summary: FinanceSummary | null;
  loading: Record<string, boolean>;
  error: string | null;
}

const initialState: FinanceState = {
  account: null,
  balance: null,
  transactions: { items: [], total: 0, page: 1, per_page: 25, total_pages: 0 },
  invoices: { items: [], total: 0, page: 1, per_page: 25, total_pages: 0 },
  payouts: { items: [], total: 0, page: 1, per_page: 25, total_pages: 0 },
  expenses: { items: [], total: 0, page: 1, per_page: 25, total_pages: 0 },
  billingRates: [],
  distributions: { items: [], total: 0, page: 1, per_page: 25, total_pages: 0 },
  selectedInvoice: null,
  expenseSummary: null,
  unbilledExpenses: [],
  summary: null,
  loading: {},
  error: null,
};

const financeReducer = (state = initialState, action: any): FinanceState => {
  switch (action.type) {
    case FINANCE_SET_ACCOUNT:
      return { ...state, account: action.payload };

    case FINANCE_SET_BALANCE:
      return { ...state, balance: action.payload };

    case FINANCE_SET_TRANSACTIONS:
      return {
        ...state,
        transactions: {
          items: action.payload.transactions,
          total: action.payload.total,
          page: action.payload.page,
          per_page: action.payload.per_page,
          total_pages: action.payload.total_pages,
        },
      };

    case FINANCE_SET_INVOICES:
      return {
        ...state,
        invoices: {
          items: action.payload.invoices,
          total: action.payload.total,
          page: action.payload.page,
          per_page: action.payload.per_page,
          total_pages: action.payload.total_pages,
        },
      };

    case FINANCE_SET_PAYOUTS:
      return {
        ...state,
        payouts: {
          items: action.payload.payouts,
          total: action.payload.total,
          page: action.payload.page,
          per_page: action.payload.per_page,
          total_pages: action.payload.total_pages,
        },
      };

    case FINANCE_SET_SUMMARY:
      return { ...state, summary: action.payload };

    case FINANCE_SET_EXPENSES:
      return {
        ...state,
        expenses: {
          items: action.payload.expenses,
          total: action.payload.total,
          page: action.payload.page,
          per_page: action.payload.per_page,
          total_pages: action.payload.total_pages,
        },
      };

    case FINANCE_SET_BILLING_RATES:
      return { ...state, billingRates: action.payload };

    case FINANCE_SET_DISTRIBUTIONS:
      return {
        ...state,
        distributions: {
          items: action.payload.distributions,
          total: action.payload.total,
          page: action.payload.page,
          per_page: action.payload.per_page,
          total_pages: action.payload.total_pages,
        },
      };

    case FINANCE_SET_SELECTED_INVOICE:
      return { ...state, selectedInvoice: action.payload };

    case FINANCE_SET_EXPENSE_SUMMARY:
      return { ...state, expenseSummary: action.payload };

    case FINANCE_SET_UNBILLED_EXPENSES:
      return { ...state, unbilledExpenses: action.payload };

    case FINANCE_SET_LOADING:
      return {
        ...state,
        loading: { ...state.loading, [action.payload.key]: action.payload.value },
      };

    case FINANCE_SET_ERROR:
      return { ...state, error: action.payload };

    case FINANCE_CLEAR:
      return initialState;

    default:
      return state;
  }
};

export default financeReducer;
